// Helper para labels dos índices climáticos

export type ClimateIndexType = 'nino3.4' | 'aao' | 'default';

interface LabelRange {
  min: number;
  max: number;
  label: string;
  color: string;
}

// Labels para NINO 3.4
const ninoLabels: LabelRange[] = [
  {
    min: 2.0, max: Infinity, label: 'El Niño Muito Forte (≥ 2.0)', color: '#B02418',
  },
  {
    min: 1.5, max: 2.0, label: 'El Niño Forte (1.5 a 2.0)', color: '#B02418',
  },
  {
    min: 1.0, max: 1.5, label: 'El Niño Moderado (1.0 a 1.5)', color: '#D1483C',
  },
  {
    min: 0.5, max: 1.0, label: 'El Niño Fraco (0.5 a 1.0)', color: '#E0632E',
  },
  {
    min: -0.5, max: 0.5, label: 'Neutro ([-0.5, 0.5])', color: '#9AA6B3',
  },
  {
    min: -1.0, max: -0.5, label: 'La Niña Fraca (-1.0 a -0.5)', color: '#7FB6DA',
  },
  {
    min: -1.5, max: -1.0, label: 'La Niña Moderada (-1.5 a -1.0)', color: '#2F6FAE',
  },
  {
    min: -2.0, max: -1.5, label: 'La Niña Forte (-2.0 a -1.5)', color: '#2F6FAE',
  },
  {
    min: -Infinity, max: -2.0, label: 'La Niña Muito Forte (≤ -2.0)', color: '#17324E',
  },
];

// Labels para AAO
const aaoLabels: LabelRange[] = [
  {
    min: 2.0, max: Infinity, label: 'Fase Positiva Forte (> 2.0)', color: '#B02418',
  },
  {
    min: 1.0, max: 2.0, label: 'Fase Positiva Moderada (1.0 a 2.0)', color: '#D1483C',
  },
  {
    min: 0.1, max: 1.0, label: 'Fase Positiva Fraca (0.1 a 1.0)', color: '#E0632E',
  },
  {
    min: -0.1, max: 0.1, label: 'Neutro ([-0.1, 0.1])', color: '#9AA6B3',
  },
  {
    min: -1.0, max: -0.1, label: 'Fase Negativa Fraca (-1.0 a -0.1)', color: '#7FB6DA',
  },
  {
    min: -2.0, max: -1.0, label: 'Fase Negativa Moderada (-2.0 a -1.0)', color: '#2F6FAE',
  },
  {
    min: -Infinity, max: -2.0, label: 'Fase Negativa Forte (< -2.0)', color: '#17324E',
  },
];

// Labels para demais índices (IOD, TNA, TSA, AMO, PDO)
const defaultLabels: LabelRange[] = [
  {
    min: 0.4, max: Infinity, label: 'Positivo (≥ 0.4)', color: '#D1483C',
  },
  {
    min: 0.1, max: 0.4, label: 'Viés Positivo (0.1 a 0.4)', color: '#E0632E',
  },
  {
    min: -0.1, max: 0.1, label: 'Neutro ([-0.1, 0.1])', color: '#9AA6B3',
  },
  {
    min: -0.4, max: -0.1, label: 'Viés Negativo (-0.4 a -0.1)', color: '#7FB6DA',
  },
  {
    min: -Infinity, max: -0.4, label: 'Negativo (≤ -0.4)', color: '#2F6FAE',
  },
];

function getLabelsForType(indexType: ClimateIndexType): LabelRange[] {
  switch (indexType) {
    case 'nino3.4':
      return ninoLabels;
    case 'aao':
      return aaoLabels;
    case 'default':
    default:
      return defaultLabels;
  }
}

/**
 * Retorna a label apropriada para um valor de índice climático
 */
export function getClimateIndexLabel(value: number, indexType: ClimateIndexType): string {
  const labels = getLabelsForType(indexType);
  const matchingRange = labels.find((range) => value >= range.min && value < range.max);
  return matchingRange ? matchingRange.label : 'Desconhecido';
}

/**
 * Retorna a cor associada a um valor de índice climático
 */
export function getClimateIndexColor(value: number, indexType: ClimateIndexType): string {
  const labels = getLabelsForType(indexType);
  const matchingRange = labels.find((range) => value >= range.min && value < range.max);
  return matchingRange ? matchingRange.color : '#9AA6B3';
}

/**
 * Retorna todas as labels possíveis para um tipo de índice
 */
export function getAllLabelsForIndex(indexType: ClimateIndexType): string[] {
  return getLabelsForType(indexType).map((range) => range.label);
}

/**
 * Retorna os nomes dos meses
 */
export const MONTH_NAMES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

/**
 * Retorna o nome do mês dado seu número (1-12)
 */
export function getMonthName(monthNumber: number): string {
  return MONTH_NAMES[monthNumber - 1] || 'Mês Inválido';
}
